import { MEDIA_EVENTS, type PendingEventData } from 'components-machine';
import _, { uniq } from 'lodash';
import { useCallback, useContext, useRef } from 'react';
import { PollContext } from '../store/withContext';

export const defaultEvents = [
  { event: MEDIA_EVENTS.START, condition: (value: number) => value > 0 },
  { event: MEDIA_EVENTS.FIRST_QUARTILE, condition: (value: number, duration: number) => value >= duration * 0.25 },
  { event: MEDIA_EVENTS.MIDPOINT, condition: (value: number, duration: number) => value >= duration * 0.5 },
  { event: MEDIA_EVENTS.THIRD_QUARTILE, condition: (value: number, duration: number) => value >= duration * 0.75 },
  { event: MEDIA_EVENTS.COMPLETE, condition: (value: number, duration: number) => value >= duration },
] as const;

export const useTrackMediaProgress = () => {
  const trackedEvents = useRef<Array<string>>([]);
  const { interpreter } = useContext(PollContext);

  const handleTimeUpdate = useCallback(
    (questionId: string, currentTime: number, duration: number) => {
      for (let i = defaultEvents.length - 1; i >= 0; i--) {
        const { event, condition } = defaultEvents[i] ?? {};

        if (condition?.(currentTime, duration) && event && !trackedEvents.current.includes(event)) {
          trackedEvents.current = defaultEvents.map(({ event }) => event).slice(0, i + 1);

          interpreter.send({ type: 'mediaProgress', mediaProgress: event, questionId });
        }
      }
    },
    [interpreter],
  );

  return handleTimeUpdate;
};

type PendingEvent = [string, string, PendingEventData];

let pendingEvents: PendingEvent[] = [];

export const addPendingEvent = (eventName: string, questionId: string, data: PendingEventData) => {
  pendingEvents.push([eventName, questionId, data]);
};

export const resolvePendingEvents = (eventName: string, questionId: string) => {
  let dataToSend: PendingEventData | null = null;

  for (const [name, id, data] of pendingEvents) {
    if (name !== eventName || id !== questionId) continue;
    dataToSend = {
      ..._.mergeWith(dataToSend || {}, data, (objValue, srcValue) => {
        if (_.isArray(objValue)) {
          return objValue.concat(srcValue);
        }
      }),
    };
  }

  pendingEvents = pendingEvents.filter(([name, id]) => name !== eventName || id !== questionId);

  return dataToSend;
};

export const resolveAllPendingEvents = (eventName: string) => {
  const questionIds = uniq(pendingEvents.filter(([name]) => name === eventName).map(([, id]) => id));

  return questionIds.map(questionId => ({ questionId, pendingEvents: resolvePendingEvents(eventName, questionId) }));
};

export const hasPendingEvents = () => pendingEvents.length > 0;
